import { useEffect, useState } from 'react';

// A small breakpoint map similar to common design systems (px values)
// These approximate typical Arco/CSS breakpoints: xs, sm, md, lg, xl
const BREAKPOINTS = {
    xs: 0,
    sm: 768,
    md: 900,
    lg: 1200,
    xl: 1500,
};

function px(value) {
    return `${value}px`;
}

function mqUp(key) {
    const min = BREAKPOINTS[key] ?? 0;
    return `(min-width: ${px(min)})`;
}

function mqDown(key) {
    const max = (BREAKPOINTS[key] ? BREAKPOINTS[key] - 0.02 : 0);
    return `(max-width: ${px(max)})`;
}

function mqBetween(start, end) {
    const min = BREAKPOINTS[start] ?? 0;
    const max = (BREAKPOINTS[end] ? BREAKPOINTS[end] - 0.02 : null);
    if (max) return `(min-width: ${px(min)}) and (max-width: ${px(max)})`;
    return `(min-width: ${px(min)})`;
}

function mqOnly(key) {
    const min = BREAKPOINTS[key] ?? 0;
    // find next breakpoint
    const keys = Object.keys(BREAKPOINTS);
    const idx = keys.indexOf(key);
    const next = keys[idx + 1];
    if (next) {
        const max = BREAKPOINTS[next] - 0.02;
        return `(min-width: ${px(min)}) and (max-width: ${px(max)})`;
    }
    return `(min-width: ${px(min)})`;
}

// Helper to get initial match synchronously
function getInitialMatch(mediaQuery) {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(mediaQuery).matches;
}

// compatibility: keep same signature as before: useResponsive(query, start, end)
export function useResponsive(query, start, end) {
    // Compute the media query string
    let mediaQuery = '';
    if (query === 'up') mediaQuery = mqUp(start);
    else if (query === 'down') mediaQuery = mqDown(start);
    else if (query === 'between') mediaQuery = mqBetween(start, end);
    else mediaQuery = mqOnly(start);

    // Initialize with the correct value synchronously
    const [matches, setMatches] = useState(() => getInitialMatch(mediaQuery));

    useEffect(() => {
        const m = window.matchMedia(mediaQuery);
        const handler = (e) => setMatches(Boolean(e.matches));

        // Sync state in case it changed between render and effect
        setMatches(Boolean(m.matches));
        
        if (m.addEventListener) m.addEventListener('change', handler);
        else m.addListener(handler);

        return () => {
            if (m.removeEventListener) m.removeEventListener('change', handler);
            else m.removeListener(handler);
        };
    }, [mediaQuery]);

    return matches;
}

export function useWidth() {
    // Helper to get initial width key synchronously
    const getWidthKey = () => {
        if (typeof window === 'undefined') return 'xs';
        const w = window.innerWidth;
        const keys = Object.keys(BREAKPOINTS).reverse();
        const found = keys.find((key) => w >= BREAKPOINTS[key]);
        return found || 'xs';
    };

    // Initialize with the correct value synchronously
    const [widthKey, setWidthKey] = useState(() => getWidthKey());

    useEffect(() => {
        function onResize() {
            setWidthKey(getWidthKey());
        }

        // Sync state in case it changed between render and effect
        onResize();
        
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    return widthKey;
}
